// Recebimento de Faturas — cada PDF que chegou em assinatura@ e onde ele parou.
// Projeto 12 "Controle de faturas e faturamento", tarefa 5835.
//
// Quem grava é o n8n (robô de assinatura e varredura horária da caixa); esta tela
// só lê. A regra de "precisa de atenção" está em src/lib/recebimentoFaturaTela.ts
// (com testes). Acesso: adm, gerente e analista (rota, menu e RLS).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, Inbox, Loader2, MailWarning, RefreshCw, Search, XCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import InfoTip from '../../src/components/InfoTip';
import {
  MINUTOS_TRAVADO,
  ROTULO_ETAPA,
  casaBusca,
  classificar,
  estadoConferencia,
  formatarDuracao,
  resumir,
  type Conferencia,
  type EtapaRecebimento,
  type Recebimento,
  type Situacao,
} from '../../src/lib/recebimentoFaturaTela';

const TAMANHO_PAGINA_BANCO = 1000;
const LINHAS_POR_PAGINA = 100;
const PERIODOS = [
  { dias: 3, rotulo: 'Últimos 3 dias' },
  { dias: 7, rotulo: 'Últimos 7 dias' },
  { dias: 30, rotulo: 'Últimos 30 dias' },
];

const CAMPOS = [
  'id', 'etapa', 'recebido_em', 'capturado_em', 'processamento_iniciado_em', 'processado_em', 'tentativas',
  'erro_etapa', 'erro_codigo', 'erro_mensagem', 'uc_codigo', 'uc_codigo_norm', 'mes_referencia', 'assunto',
  'anexo_nome', 'fatura_id', 'n8n_execution_id', 'historico',
].join(', ');

type Linha = Recebimento & { n8n_execution_id?: string | null; historico?: any[] };

const ESTILO_CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200';

const ESTILO_SITUACAO: Record<Situacao, { classe: string; Icone: typeof CheckCircle2 }> = {
  atencao: { classe: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', Icone: XCircle },
  andamento: { classe: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icone: Loader2 },
  ok: { classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icone: CheckCircle2 },
};

const dataHora = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

async function buscarTudo<T>(montar: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const todas: T[] = [];
  for (let de = 0; ; de += TAMANHO_PAGINA_BANCO) {
    const { data, error } = await montar(de, de + TAMANHO_PAGINA_BANCO - 1);
    if (error) throw error;
    todas.push(...(data ?? []));
    if (!data || data.length < TAMANHO_PAGINA_BANCO) return todas;
  }
}

type FiltroRapido = '' | 'atencao' | 'falha' | 'nao_capturado' | 'travado';

interface CartaoProps {
  titulo: string;
  valor: string | number;
  sub?: string;
  tom?: 'ok' | 'alerta' | 'perigo' | 'neutro';
  filtro?: FiltroRapido;
  ativo?: boolean;
  onAlternar?: (f: FiltroRapido) => void;
  dica?: React.ReactNode;
}

const COR_TOM: Record<NonNullable<CartaoProps['tom']>, string> = {
  ok: 'text-emerald-700 dark:text-emerald-400',
  alerta: 'text-amber-700 dark:text-amber-400',
  perigo: 'text-red-700 dark:text-red-400',
  neutro: 'text-gray-900 dark:text-white',
};

/**
 * O cartão com filtro é clicável inteiro, mas o gatilho da dica também é um
 * botão — e botão dentro de botão é HTML inválido: o clique na dica subia para
 * o cartão e ligava o filtro sem querer, e o leitor de tela lia os dois juntos.
 * Aqui o cartão é uma div e quem recebe o clique é um botão sobreposto
 * (`absolute inset-0`), com a dica acima dele (`relative z-10`). Visualmente
 * idêntico, com dois alvos independentes para clique e teclado.
 */
const Cartao: React.FC<CartaoProps> = ({ titulo, valor, sub, tom = 'neutro', filtro, ativo = false, onAlternar, dica }) => {
  const clicavel = !!filtro && !!onAlternar;
  return (
    <div className={`relative rounded-2xl border bg-white p-4 text-left shadow-sm dark:bg-gray-800/60 ${ativo ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 dark:border-gray-700'} ${clicavel ? 'transition-colors hover:border-primary focus-within:border-primary' : ''}`}>
      {clicavel && (
        <button
          type="button"
          onClick={() => onAlternar!(filtro!)}
          aria-pressed={ativo}
          aria-label={`Filtrar por ${titulo}`}
          className="absolute inset-0 z-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      )}
      <p className="pointer-events-none relative z-10 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {titulo}{dica && <span className="pointer-events-auto"><InfoTip label={titulo}>{dica}</InfoTip></span>}
      </p>
      <p className={`pointer-events-none relative z-10 mt-1 text-2xl font-bold tabular-nums ${COR_TOM[tom]}`}>{typeof valor === 'number' ? valor.toLocaleString('pt-BR') : valor}</p>
      {sub && <p className="pointer-events-none relative z-10 mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
    </div>
  );
};

interface Carga {
  linhas: Linha[];
  conferencias: Conferencia[];
  carregadaEm: Date;
}

const RecebimentoFaturasView: React.FC = () => {
  const [dias, setDias] = useState(7);
  const [carga, setCarga] = useState<Carga | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [etapa, setEtapa] = useState<EtapaRecebimento | ''>('');
  const [rapido, setRapido] = useState<FiltroRapido>('atencao');
  const [aberta, setAberta] = useState<number | null>(null);
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async (diasAlvo: number) => {
    setCarregando(true);
    setErro(null);
    try {
      const desde = new Date(Date.now() - diasAlvo * 864e5).toISOString();
      const [linhas, conf] = await Promise.all([
        buscarTudo<Linha>((de, ate) =>
          supabase.from('fatura_recebimentos_email').select(CAMPOS)
            .gte('recebido_em', desde).order('recebido_em', { ascending: false }).order('id').range(de, ate) as any),
        supabase.from('fatura_conferencias_email')
          .select('id, iniciada_em, concluida_em, janela_inicio, emails_encontrados, anexos_encontrados, nao_capturados, erro')
          .order('iniciada_em', { ascending: false }).limit(10),
      ]);
      if (conf.error) throw conf.error;
      setCarga({ linhas, conferencias: (conf.data ?? []) as Conferencia[], carregadaEm: new Date() });
    } catch (e: any) {
      console.error('Recebimento de faturas:', e);
      setErro('Não foi possível carregar os recebimentos. Tente novamente em instantes.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(dias); }, [dias, carregar]);
  useEffect(() => { setPagina(1); setAberta(null); }, [busca, etapa, rapido, dias]);

  const agora = useMemo(() => carga?.carregadaEm ?? new Date(), [carga]);
  const resumo = useMemo(() => (carga ? resumir(carga.linhas, agora) : null), [carga, agora]);
  const conferencia = useMemo(() => (carga ? estadoConferencia(carga.conferencias, agora) : null), [carga, agora]);

  const filtradas = useMemo(() => {
    if (!carga) return [];
    return carga.linhas.filter((l) => {
      const c = classificar(l, agora);
      if (rapido === 'atencao' && c.situacao !== 'atencao') return false;
      if (rapido === 'falha' && l.etapa !== 'falha') return false;
      if (rapido === 'nao_capturado' && l.etapa !== 'nao_capturado') return false;
      if (rapido === 'travado' && !c.travado) return false;
      if (etapa && l.etapa !== etapa) return false;
      return casaBusca(l, busca);
    });
  }, [carga, agora, rapido, etapa, busca]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / LINHAS_POR_PAGINA));
  const visiveis = filtradas.slice((pagina - 1) * LINHAS_POR_PAGINA, pagina * LINHAS_POR_PAGINA);
  const alternarRapido = (f: FiltroRapido) => setRapido((atual) => (atual === f ? '' : f));
  const limpar = () => { setBusca(''); setEtapa(''); setRapido(''); };

  if (!carga && carregando) return <LoadingSpinner label="Lendo os recebimentos de fatura..." />;

  if (!carga && erro) {
    return (
      <div className="p-6">
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          <p className="font-semibold">Não foi possível carregar os recebimentos.</p>
          <p className="mt-1 text-sm">{erro}</p>
          <button onClick={() => carregar(dias)} disabled={carregando} aria-busy={carregando} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60">
            {carregando && <Loader2 size={14} className="animate-spin" />} {carregando ? 'Tentando...' : 'Tentar de novo'}
          </button>
        </div>
      </div>
    );
  }

  const aviso = (() => {
    if (!conferencia) return null;
    const { estado, ultima, ultimaConcluida } = conferencia;
    const ultimaOk = ultimaConcluida ? `Última conferência completa: ${dataHora(ultimaConcluida.concluida_em)}.` : 'Nenhuma conferência completa até agora.';
    if (estado === 'nunca') {
      return { tom: 'alerta', texto: 'A conferência da caixa ainda não rodou. Até ela rodar, e-mail que o robô não pegou não aparece aqui.' };
    }
    if (estado === 'incompleta') {
      return { tom: 'perigo', texto: `A conferência da caixa de ${dataHora(ultima!.iniciada_em)} não terminou${ultima!.erro ? ` (${ultima!.erro})` : ''}. Pode haver fatura na caixa que não aparece aqui. ${ultimaOk}` };
    }
    if (estado === 'atrasada') {
      return { tom: 'perigo', texto: `A conferência da caixa não roda desde ${dataHora(ultima!.concluida_em)}; ela deveria rodar de hora em hora. Pode haver fatura na caixa que não aparece aqui.` };
    }
    return null;
  })();

  const infoConferencia = conferencia?.ultimaConcluida
    ? `Caixa conferida às ${dataHora(conferencia.ultimaConcluida.concluida_em)}: ${conferencia.ultimaConcluida.anexos_encontrados ?? 0} PDF(s) desde ${dataHora(conferencia.ultimaConcluida.janela_inicio)}, ${conferencia.ultimaConcluida.nao_capturados ?? 0} não capturado(s)`
    : null;

  return (
    <div className="space-y-5 p-4 pb-24 sm:p-6 sm:pb-24">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
            <Inbox size={22} className="text-primary" />
            Recebimento de Faturas
            <InfoTip label="De onde vêm estes dados">
              <p className="mb-2">
                Cada linha é um <strong>PDF de fatura</strong> que chegou no e-mail <strong>assinatura@vigorenergy.com.br</strong>. O robô registra a chegada
                e cada etapa: leitura do PDF, envio ao site e gravação da fatura.
              </p>
              <p className="mb-2">
                De hora em hora, uma <strong>conferência da caixa</strong> compara os e-mails das últimas 72 h com esta lista. O que chegou e o robô não pegou
                (por exemplo, e-mail aberto antes do robô) aparece como <strong>Não capturado</strong>.
              </p>
              <p>
                <strong>Parado</strong> = em processamento há mais de {MINUTOS_TRAVADO} minutos: a execução do robô caiu no meio.
              </p>
            </InfoTip>
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {infoConferencia ?? 'Caixa ainda não conferida'}
            {carga && <> · posição de {dataHora(carga.carregadaEm.toISOString())}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="recebimento-periodo">Período</label>
          <select id="recebimento-periodo" value={dias} onChange={(e) => setDias(Number(e.target.value))} className={ESTILO_CAMPO}>
            {PERIODOS.map((p) => <option key={p.dias} value={p.dias}>{p.rotulo}</option>)}
          </select>
          <button
            onClick={() => carregar(dias)}
            disabled={carregando}
            aria-busy={carregando}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw size={15} className={carregando ? 'animate-spin' : ''} /> {carregando ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </header>

      {erro && carga && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          Não foi possível atualizar: {erro}. Os números abaixo são da última carga.
        </div>
      )}

      {aviso && (
        <div role={aviso.tom === 'perigo' ? 'alert' : 'status'} className={`flex gap-2 rounded-xl border p-3 text-sm ${aviso.tom === 'perigo'
          ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200'
          : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200'}`}>
          <MailWarning size={18} className="mt-0.5 shrink-0" />
          <p>{aviso.texto}</p>
        </div>
      )}

      {resumo && (
        <section aria-label="Resumo" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Cartao titulo="Precisa de atenção" valor={resumo.atencao} tom={resumo.atencao ? 'perigo' : 'ok'} filtro="atencao" ativo={rapido === 'atencao'} onAlternar={alternarRapido}
            sub="Falha + não capturado + parado" />
          <Cartao titulo="Falhas" valor={resumo.falha} tom={resumo.falha ? 'perigo' : 'neutro'} filtro="falha" ativo={rapido === 'falha'} onAlternar={alternarRapido} dica={
            <p>O robô tentou e não conseguiu: PDF ilegível, leitura fora do ar, site do PDF fora ou erro ao gravar. Depois de corrigir, reprocesse a execução no n8n; a linha guarda todas as tentativas.</p>
          } />
          <Cartao titulo="Não capturados" valor={resumo.naoCapturado} tom={resumo.naoCapturado ? 'perigo' : 'neutro'} filtro="nao_capturado" ativo={rapido === 'nao_capturado'} onAlternar={alternarRapido} dica={
            <p>Chegou na caixa e o robô nunca pegou. A fatura precisa ser lançada à mão ou reenviada ao robô.</p>
          } />
          <Cartao titulo="Parados" valor={resumo.travado} tom={resumo.travado ? 'alerta' : 'neutro'} filtro="travado" ativo={rapido === 'travado'} onAlternar={alternarRapido}
            sub={`Mais de ${MINUTOS_TRAVADO} min sem terminar`} />
          <Cartao titulo="Processados" valor={resumo.processado} tom="ok" sub={`${resumo.duplicata} duplicata(s) · ${resumo.total} PDF(s) no período`} />
          <Cartao titulo="Tempo até gravar" valor={resumo.medianaMinutos === null ? '—' : formatarDuracao(resumo.medianaMinutos)} sub="Mediana: chegada no e-mail → fatura gravada" />
        </section>
      )}

      <section className="flex flex-wrap items-center gap-2" aria-label="Filtros">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar UC, arquivo ou erro" aria-label="Buscar UC, arquivo ou erro" className={`${ESTILO_CAMPO} w-full pl-9`} />
        </div>
        <select value={etapa} onChange={(e) => setEtapa(e.target.value as EtapaRecebimento | '')} aria-label="Etapa" className={ESTILO_CAMPO}>
          <option value="">Todas as etapas</option>
          {(Object.keys(ROTULO_ETAPA) as EtapaRecebimento[]).map((k) => <option key={k} value={k}>{ROTULO_ETAPA[k]}</option>)}
        </select>
        {(busca || etapa || rapido) && (
          <button type="button" onClick={limpar} className="text-sm font-medium text-primary hover:underline">Ver todos</button>
        )}
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400" aria-live="polite">{filtradas.length.toLocaleString('pt-BR')} PDF(s)</span>
      </section>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
            <tr>
              <th className="w-8 px-3 py-2"><span className="sr-only">Histórico</span></th>
              <th className="px-3 py-2">Chegou</th>
              <th className="px-3 py-2">UC / Mês</th>
              <th className="px-3 py-2">Etapa</th>
              <th className="px-3 py-2">O que houve</th>
              <th className="px-3 py-2">Fatura</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visiveis.map((l) => {
              const c = classificar(l, agora);
              const { classe, Icone } = ESTILO_SITUACAO[c.situacao];
              const expandida = aberta === l.id;
              const historico = Array.isArray(l.historico) ? l.historico : [];
              return (
                <React.Fragment key={l.id}>
                  <tr className="align-top hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => setAberta(expandida ? null : l.id)} aria-expanded={expandida} aria-controls={`hist-${l.id}`}
                        aria-label={`${expandida ? 'Esconder' : 'Ver'} histórico do PDF ${l.anexo_nome ?? l.id}`}
                        className="rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 dark:hover:text-gray-200">
                        {expandida ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700 dark:text-gray-200">
                      <p>{dataHora(l.recebido_em)}</p>
                      {l.processado_em && <p className="text-gray-400">gravada {dataHora(l.processado_em)}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-mono text-xs text-gray-900 dark:text-white">{l.uc_codigo_norm ?? l.uc_codigo ?? '—'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{l.mes_referencia ?? 'mês não identificado'}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${classe}`}>
                        <Icone size={12} className={c.situacao === 'andamento' ? 'animate-spin' : ''} aria-hidden /> {c.travado ? 'Parado' : ROTULO_ETAPA[l.etapa as EtapaRecebimento] ?? l.etapa}
                      </span>
                      {(l.tentativas ?? 0) > 1 && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{l.tentativas} tentativas</p>}
                    </td>
                    <td className="max-w-[360px] px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                      <p className={c.situacao === 'atencao' ? 'font-semibold text-red-700 dark:text-red-400' : ''}>{c.motivo}</p>
                      {l.erro_mensagem && <p className="mt-0.5 line-clamp-2 break-words" title={l.erro_mensagem}>{l.erro_mensagem}</p>}
                      {l.anexo_nome && <p className="mt-0.5 text-gray-400">{l.anexo_nome}</p>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                      {l.fatura_id ? <p>#{l.fatura_id}</p> : <p className="text-gray-400">—</p>}
                    </td>
                  </tr>
                  {expandida && (
                    <tr id={`hist-${l.id}`} className="bg-gray-50/60 dark:bg-gray-900/30">
                      <td />
                      <td colSpan={5} className="px-3 py-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Histórico</p>
                        {historico.length === 0 ? <p className="text-xs text-gray-500">Sem histórico.</p> : (
                          <ol className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                            {historico.map((h: any, i: number) => (
                              <li key={i} className="flex flex-wrap gap-x-2">
                                <span className="tabular-nums text-gray-500 dark:text-gray-400">{dataHora(h.em)}</span>
                                <span className="font-medium">{ROTULO_ETAPA[h.etapa as EtapaRecebimento] ?? h.etapa}</span>
                                {h.evento === 'reenviado' && <span className="text-gray-500">(e-mail lido de novo)</span>}
                                {h.ignorada && <span className="text-gray-500">(ignorada: já estava {ROTULO_ETAPA[h.etapa_mantida as EtapaRecebimento] ?? h.etapa_mantida})</span>}
                                {h.erro_codigo && <span className="text-red-700 dark:text-red-400">{h.erro_codigo}</span>}
                                {h.erro_mensagem && <span className="break-all text-gray-500 dark:text-gray-400">{String(h.erro_mensagem).slice(0, 300)}</span>}
                                {h.execution_id && <span className="text-gray-400">execução {h.execution_id}</span>}
                              </li>
                            ))}
                          </ol>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {visiveis.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  {rapido === 'atencao' && !busca && !etapa ? (
                    <><CheckCircle2 size={22} className="mx-auto mb-2 text-emerald-500" />Nenhum PDF precisando de atenção no período.</>
                  ) : carga?.linhas.length === 0 ? (
                    <><Clock size={22} className="mx-auto mb-2 text-gray-300" />Nenhum PDF registrado no período.</>
                  ) : (
                    <><AlertTriangle size={22} className="mx-auto mb-2 text-gray-300" />Nenhum PDF com esses filtros.</>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <nav className="flex items-center justify-end gap-2 text-sm" aria-label="Paginação">
          <button type="button" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)} className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">Anterior</button>
          <span className="text-gray-500 dark:text-gray-400">Página {pagina} de {totalPaginas}</span>
          <button type="button" disabled={pagina === totalPaginas} onClick={() => setPagina((p) => p + 1)} className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-40 dark:border-gray-700 dark:text-gray-200">Próxima</button>
        </nav>
      )}
    </div>
  );
};

export default RecebimentoFaturasView;
